import { normalizeUser } from '@hr/common/utils.jsx'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Form, Modal, Row, Tab, Tabs } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Button } from '../common/Button.tsx'
import Switch from '../common/Switch.jsx'

export default function EditUserModal({ show, onHide, user, onSave }) {
	const [key, setKey] = useState('user')
	const { t } = useTranslation()

	const [formData, setFormData] = useState({})
	const [customRights, setCustomRights] = useState(false)
	const [role, setRole] = useState('')

	const isEquipmentRole = role === 'EQUIPMENT'
	const isEmployeeRole = role === 'EMPLOYEE'

	const rolePresets = useMemo(
		() => ({
			ADMIN: {
				persons: true,
				personattachments: true,
				salary: true,
				knowledge: true,
				knowledgeadmin: true,
				contracts: true,
				contractattachments: true,
				discussions: true,
				discussionsadmin: true,
				statistics: true,
				equipment: true,
				organization: true,
				presalarylevel: true,
				paymentsapproval: true,
				admin: true,
			},

			SUPERVISOR: {
				persons: true,
				personattachments: true,
				salary: true,
				knowledge: true,
				knowledgeadmin: true,
				contracts: true,
				contractattachments: true,
				discussions: true,
				discussionsadmin: false,
				statistics: true,
				equipment: true,
				organization: true,
				presalarylevel: false,
				paymentsapproval: false,
				admin: false,
			},

			HRUSER: {
				// Lesser ADMIN
				persons: true,
				personattachments: false,
				salary: false,
				knowledge: true,
				knowledgeadmin: false,
				contracts: false,
				contractattachments: false,
				discussions: false,
				discussionsadmin: false,
				statistics: false,
				equipment: true,
				organization: false,
				presalarylevel: false,
				paymentsapproval: false,
				admin: false,
			},

			CLERK: {
				// Payroll clerk
				persons: true,
				personattachments: false,
				salary: true,
				knowledge: false,
				knowledgeadmin: false,
				contracts: false,
				contractattachments: false,
				discussions: false,
				discussionsadmin: false,
				statistics: false,
				equipment: false,
				organization: false,
				presalarylevel: true,
				paymentsapproval: true,
				admin: false,
			},

			EQUIPMENT: {
				// Equipment manager
				persons: false,
				personattachments: false,
				salary: false,
				knowledge: false,
				knowledgeadmin: false,
				contracts: false,
				contractattachments: false,
				discussions: false,
				discussionsadmin: false,
				statistics: false,
				equipment: true,
				organization: false,
				presalarylevel: false,
				paymentsapproval: false,
				admin: false,
			},

			EMPLOYEE: {
				// User portal
				persons: false,
				personattachments: false,
				salary: false,
				knowledge: false,
				knowledgeadmin: false,
				contracts: false,
				contractattachments: false,
				discussions: false,
				discussionsadmin: false,
				statistics: false,
				equipment: false,
				organization: false,
				presalarylevel: false,
				paymentsapproval: false,
				admin: false,
			},
		}),
		[],
	)

	useEffect(() => {
		if (!user) return

		const normalized = normalizeUser(user)

		setFormData(normalized)
		setRole(normalized.role || '')
		setCustomRights(!!normalized.customrights)
	}, [user])

	const emptyRights = useMemo(
		() => ({
			persons: false,
			personattachments: false,
			salary: false,
			knowledge: false,
			knowledgeadmin: false,
			contracts: false,
			contractattachments: false,
			discussions: false,
			discussionsadmin: false,
			statistics: false,
			equipment: false,
			organization: false,
			presalarylevel: false,
			paymentsapproval: false,
			admin: false,
		}),
		[],
	)

	const applyPreset = useCallback(
		(r) => {
			const preset = rolePresets[r]
			if (!preset) return

			setFormData((prev) => ({
				...prev,
				...emptyRights,
				...preset,
			}))
		},
		[rolePresets, emptyRights],
	)

	useEffect(() => {
		if (!role || customRights) return
		applyPreset(role)
	}, [role, customRights, applyPreset])

	const handleChange = (field, value) => {
		setFormData((prev) => ({ ...prev, [field]: value }))
	}

	const handleSave = async () => {
		const payload = {
			id: formData.id,
			user: formData.user,
			email: formData.email,
			fullname: formData.fullname,
			role,

			...Object.fromEntries(Object.keys(emptyRights).map((k) => [k, !!formData[k]])),

			salarynumber: formData.salarynumber,
			employmentstatus: formData.employmentstatus,
			expirydate: formData.expirydate,

			customrights: !!customRights,
		}

		await onSave(payload)
		onHide()
	}

	return (
		<Modal show={show} onHide={onHide} size="lg" backdrop="static">
			<Modal.Header closeButton>
				<Modal.Title>
					{user?.user
						? `${t('EditUserTitle', 'Muokkaa käyttäjää')}: ${user.fullname || user.user}`
						: t('CreateNewUserTitle', 'Luo uusi käyttäjä')}
				</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<Tabs activeKey={key} onSelect={(k) => setKey(k)}>
					<Tab eventKey="user" title={t('UserTab', 'Käyttäjä')}>
						<Form className="mt-3">
							<Row className="mb-3">
								<Form.Group as={Col} md={6}>
									<Form.Label>{t('Username', 'Käyttäjätunnus')}</Form.Label>
									<Form.Control
										value={formData.user || ''}
										onChange={(e) => handleChange('user', e.target.value)}
										disabled={user.fullname}
									/>
								</Form.Group>
								<Form.Group as={Col} md={6}>
									<Form.Label>{t('Email', 'Sähköposti')}</Form.Label>
									<Form.Control
										value={formData.email || ''}
										onChange={(e) => handleChange('email', e.target.value)}
										disabled={user.fullname}
									/>
								</Form.Group>
							</Row>
							<Row>
								<Form.Group as={Col} md={4}>
									<Form.Label>{t('PersonNumber', 'Henkilönumero')}</Form.Label>
									<Form.Control
										value={formData.salarynumber || ''}
										onChange={(e) => handleChange('salarynumber', e.target.value)}
										disabled
									/>
								</Form.Group>
							</Row>
						</Form>
					</Tab>

					<Tab eventKey="roles" title={t('RolesAndPermissions', 'Rooli ja käyttöoikeudet')}>
						<Form className="mt-3">
							<Row className="mb-3 align-items-end">
								<Col md={4}>
									<Form.Label>{t('Role', 'Rooli')}</Form.Label>

									<Form.Select value={role} onChange={(e) => setRole(e.target.value)} disabled={customRights}>
										<option value="">{t('SelectRole', 'Valitse rooli')}</option>

										<option value="ADMIN">{t('RoleAdmin', 'Pääkäyttäjä')}</option>
										<option value="SUPERVISOR">{t('RoleSupervisor', 'Esihenkilö')}</option>
										<option value="HRUSER">{t('RoleHRUser', 'HR käyttäjä')}</option>
										<option value="CLERK">{t('RolePayrollClerk', 'Palkanlaskija')}</option>
										<option value="EQUIPMENT">{t('RoleEquipmentManagement', 'Laitehallinta')}</option>
										<option value="EMPLOYEE">{t('RoleEmployee', 'Työntekijä')}</option>
									</Form.Select>
								</Col>

								{!isEquipmentRole && !isEmployeeRole && (
									<Col md={4}>
										<Form.Check
											type="switch"
											label={t('PersonallyCustomizedRights', 'Henkilökohtaisesti muokatut oikeudet')}
											checked={customRights}
											onChange={(e) => {
												const checked = e.target.checked
												setCustomRights(checked)

												if (checked) {
													setFormData((prev) => ({
														...emptyRights,
														...prev,
													}))
												} else if (role) {
													applyPreset(role)
												}
											}}
										/>
									</Col>
								)}
							</Row>
							{!isEquipmentRole && !isEmployeeRole && (
								<Row className="mb-3">
									<Col md={6}>
										<h6>{t('Persons', 'Henkilöt')}</h6>

										<Row className="align-items-center mb-2">
											<Col xs={10}>
												<small className="text-muted">
													{t(
														'Henkilötiedot',
														'Enables viewing and modification of personal details of employees, including contact information and emergency contacts. (Henkilötiedot)',
													)}
												</small>
											</Col>
											<Col xs={2} className="text-end">
												<Switch
													id="persons"
													checked={!!formData.persons}
													onChange={(e) => handleChange('persons', e.target.checked)}
													disabled={!customRights}
												/>
											</Col>
										</Row>

										<Row className="align-items-center mb-2">
											<Col xs={10}>
												<small className="text-muted">
													{t(
														'Palkkatiedot',
														'Enables management and modification of configurations related to employee salaries. (Palkkatiedot)',
													)}
												</small>
											</Col>
											<Col xs={2} className="text-end">
												<Switch
													id="salary"
													checked={!!formData.salary}
													onChange={(e) => handleChange('salary', e.target.checked)}
													disabled={!customRights}
												/>
											</Col>
										</Row>
										<Row className="align-items-center mb-2">
											<Col xs={10}>
												<small className="text-muted">
													{t('Henkilöliitteet', 'Allows viewing and editing attachments saved for a person.')}
												</small>
											</Col>
											<Col xs={2} className="text-end">
												<Switch
													id="personattachments"
													checked={!!formData.personattachments}
													onChange={(e) => handleChange('personattachments', e.target.checked)}
													disabled={!customRights}
												/>
											</Col>
										</Row>
									</Col>

									<Col md={6}>
										<h6>{t('Competences', 'Osaaminen')}</h6>

										<Row className="align-items-center mb-2">
											<Col xs={10}>
												<small className="text-muted">
													{t(
														'Osaaminen',
														'Enables viewing and modification of configurations related to employee skills and competencies. (Osaaminen)',
													)}
												</small>
											</Col>
											<Col xs={2} className="text-end">
												<Switch
													id="knowledge"
													checked={!!formData.knowledge}
													onChange={(e) => handleChange('knowledge', e.target.checked)}
													disabled={!customRights}
												/>
											</Col>
										</Row>

										<Row className="align-items-center mb-2">
											<Col xs={10}>
												<small className="text-muted">
													{t('OsaamisenAsetukset', 'Enables adding Competences list (Osaamisen asetukset)')}
												</small>
											</Col>
											<Col xs={2} className="text-end">
												<Switch
													id="knowledgeadmin"
													checked={!!formData.knowledgeadmin}
													onChange={(e) => handleChange('knowledgeadmin', e.target.checked)}
													disabled={!customRights}
												/>
											</Col>
										</Row>
									</Col>
								</Row>
							)}
							{!isEquipmentRole && !isEmployeeRole && (
								<Row className="mb-3">
									<Col md={6}>
										<h6>{t('EmploymentContractsAndCertificates', 'Työsopimukset ja sertifikaatit')}</h6>

										<Row className="align-items-center mb-2">
											<Col xs={10}>
												<small className="text-muted">
													{t(
														'Työsopimukset',
														'Enables management and modification of employee contracts and letters of reference. (Työsopimukset)',
													)}
												</small>
											</Col>
											<Col xs={2} className="text-end">
												<Switch
													id="contracts"
													checked={!!formData.contracts}
													onChange={(e) => handleChange('contracts', e.target.checked)}
													disabled={!customRights}
												/>
											</Col>
										</Row>

										<Row className="align-items-center mb-2">
											<Col xs={10}>
												<small className="text-muted">
													{t(
														'Työsopimusliitteet',
														'Allows viewing and modification of contract-related attachments and documents. (Työsopimusliitteet)',
													)}
												</small>
											</Col>
											<Col xs={2} className="text-end">
												<Switch
													id="contractattachments"
													checked={!!formData.contractattachments}
													onChange={(e) => handleChange('contractattachments', e.target.checked)}
													disabled={!customRights}
												/>
											</Col>
										</Row>
									</Col>

									<Col md={6}>
										<h6>{t('Discussions', 'Keskustelut')}</h6>

										<Row className="align-items-center mb-2">
											<Col xs={10}>
												<small className="text-muted">
													{t('Keskustelut', 'Allows management of employee discussions. (Keskustelut)')}
												</small>
											</Col>
											<Col xs={2} className="text-end">
												<Switch
													id="discussions"
													checked={!!formData.discussions}
													onChange={(e) => handleChange('discussions', e.target.checked)}
													disabled={!customRights}
												/>
											</Col>
										</Row>

										<Row className="align-items-center mb-2">
											<Col xs={10}>
												<small className="text-muted">
													{t(
														'Keskusteluasetukset',
														'Enables modification and management of discussion settings. (Keskusteluasetukset)',
													)}
												</small>
											</Col>
											<Col xs={2} className="text-end">
												<Switch
													id="discussionsadmin"
													checked={!!formData.discussionsadmin}
													onChange={(e) => handleChange('discussionsadmin', e.target.checked)}
													disabled={!customRights}
												/>
											</Col>
										</Row>
									</Col>
								</Row>
							)}
							{!isEmployeeRole && (
								<Row className="mb-3">
									{!isEquipmentRole && (
										<Col md={6}>
											<h6>{t('Statistics', 'Tilastot')}</h6>

											<Row className="align-items-center mb-2">
												<Col xs={10}>
													<small className="text-muted">
														{t(
															'Tilastot',
															'Creates and downloads comprehensive reports on employee statistics and analytics. (Tilastot)',
														)}
													</small>
												</Col>
												<Col xs={2} className="text-end">
													<Switch
														id="statistics"
														checked={!!formData.statistics}
														onChange={(e) => handleChange('statistics', e.target.checked)}
														disabled={!customRights}
													/>
												</Col>
											</Row>
										</Col>
									)}
									<Col md={6}>
										<h6>{t('EquipmentAndAccessRights', 'Välineistö ja käyttöoikeudet')}</h6>

										<Row className="align-items-center mb-2">
											<Col xs={10}>
												<small className="text-muted">
													{t(
														'VälineistöJaKäyttöoikeus',
														'Manages and configures access permissions and tools for users. (Välineistö ja käyttöoikeus)',
													)}
												</small>
											</Col>
											<Col xs={2} className="text-end">
												<Switch
													id="equipment"
													checked={!!formData.equipment}
													onChange={(e) => handleChange('equipment', e.target.checked)}
													disabled={!customRights}
												/>
											</Col>
										</Row>
									</Col>
								</Row>
							)}
							{!isEquipmentRole && !isEmployeeRole && (
								<Row className="mb-3">
									<Col md={6}>
										<h6>{t('Organization', 'Organisaatio')}</h6>

										<Row className="align-items-center mb-2">
											<Col xs={10}>
												<small className="text-muted">
													{t(
														'Organisaatio',
														'Facilitate management and modification of configurations related to organizational details. (Organisaatio)',
													)}
												</small>
											</Col>
											<Col xs={2} className="text-end">
												<Switch
													id="organization"
													checked={!!formData.organization}
													onChange={(e) => handleChange('organization', e.target.checked)}
													disabled={!customRights}
												/>
											</Col>
										</Row>
									</Col>

									<Col md={6}>
										<h6>{t('Payroll', 'Palkat')}</h6>

										<Row className="align-items-center mb-2">
											<Col xs={10}>
												<small className="text-muted">
													{t(
														'Palkkarivit',
														'Enables management and customization of payroll settings for employees. (Palkkarivit)',
													)}
												</small>
											</Col>
											<Col xs={2} className="text-end">
												<Switch
													id="presalarylevel"
													checked={!!formData.presalarylevel}
													onChange={(e) => handleChange('presalarylevel', e.target.checked)}
													disabled={!customRights}
												/>
											</Col>
										</Row>

										<Row className="align-items-center mb-2">
											<Col xs={10}>
												<small className="text-muted">
													{t('MaksujenHyväksyntä', 'Manages and approves salary payments. (Maksujen hyväksyntä)')}
												</small>
											</Col>
											<Col xs={2} className="text-end">
												<Switch
													id="paymentsapproval"
													checked={!!formData.paymentsapproval}
													onChange={(e) => handleChange('paymentsapproval', e.target.checked)}
													disabled={!customRights}
												/>
											</Col>
										</Row>
									</Col>
								</Row>
							)}
							{!isEquipmentRole && !isEmployeeRole && (
								<Row>
									<Col md={6}>
										<h6>{t('Settings', 'Asetukset')}</h6>

										<Row className="align-items-center mb-2">
											<Col xs={10}>
												<small className="text-muted">
													{t(
														'Asetukset',
														'Enables management and customization of configurations related to software. (Asetukset)',
													)}
												</small>
											</Col>
											<Col xs={2} className="text-end">
												<Switch
													id="admin"
													checked={!!formData.admin}
													onChange={(e) => handleChange('admin', e.target.checked)}
													disabled={!customRights}
												/>
											</Col>
										</Row>
									</Col>
								</Row>
							)}
						</Form>
					</Tab>
				</Tabs>
			</Modal.Body>

			<Modal.Footer style={{ display: 'flex', justifyContent: 'space-between' }}>
				<Button variant="cancel" onClick={onHide}>
					{t('Close', 'Sulje')}
				</Button>
				<Button variant="primary" onClick={handleSave}>
					{t('Save', 'Tallenna')}
				</Button>
			</Modal.Footer>
		</Modal>
	)
}
